import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  type SquareConnectionPublicCvx,
  type SquareLocation,
  type SquareSkippedObject,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { ColorBadge } from '@/components/ColorBadge';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFeatureFlagEnabled } from '@/features/feature-flags/useFeatureFlagEnabled';

export const Route = createFileRoute('/r/$restaurantId/s/square')({
  validateSearch: z.object({
    /** Why Square sent staff back without connecting (`integrations/square/oauth.ts`). */
    squareError: z.string().optional(),
  }),
  component: SquarePage,
});

/**
 * Links the venue to its Square account: Square then owns the menu, and online orders go to
 * Square's Order Manager. For venues with the `square-integration` feature flag on.
 */
function SquarePage() {
  const { restaurantId } = Route.useParams();
  const { squareError } = Route.useSearch();
  const isSquareOn = useFeatureFlagEnabled(restaurantId, 'square-integration');
  const connectionQuery = useQuery(
    api.integrations.square.getConnection,
    isSquareOn ? { restaurantId } : 'skip',
  );

  if (!isSquareOn) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Square</CardTitle>
          <CardDescription>Square isn't turned on for your venue.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  if (connectionQuery.isPending) return <LoadingIcon />;
  if (connectionQuery.isError) {
    return <ErrorAlert title="Error loading Square" message={connectionQuery.error.message} />;
  }

  const connection = connectionQuery.data;
  return (
    <>
      {squareError && <ErrorAlert title="Square isn't connected" message={squareError} />}
      {connection === null ? (
        <ConnectCard restaurantId={restaurantId} />
      ) : (
        <ConnectedCard restaurantId={restaurantId} connection={connection} />
      )}
      {connection && connection.menuSyncSkipped.length > 0 && (
        <SkippedCard skipped={connection.menuSyncSkipped} />
      )}
    </>
  );
}

const ConnectCard = ({ restaurantId }: { restaurantId: string }) => {
  const convex = useConvex();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const connect = async () => {
    setIsRedirecting(true);
    try {
      const { authorizeUrl } = await convex.mutation(api.integrations.square.startConnect, {
        restaurantId,
        // Without the query, so an earlier try's `squareError` doesn't come back
        returnUrl: `${window.location.origin}${window.location.pathname}`,
      });
      window.location.href = authorizeUrl;
    } catch (error) {
      toast.error("Couldn't open Square", { description: getUserErrorMessage(error) });
      setIsRedirecting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Square</CardTitle>
        <CardDescription>
          Connect your Square account. Your online menu will come from Square, and online orders
          will go to Square's Order Manager.
        </CardDescription>
      </CardHeader>
      <CardFooter className="border-t">
        <ActionButton loading={isRedirecting} onClick={connect}>
          Connect Square
        </ActionButton>
      </CardFooter>
    </Card>
  );
};

const ConnectedCard = ({
  restaurantId,
  connection,
}: {
  restaurantId: string;
  connection: SquareConnectionPublicCvx;
}) => {
  const convex = useConvex();
  // Without a location, staff pick one straight away
  const [isPickingLocation, setIsPickingLocation] = useState(connection.location === null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConfirmingDisconnect, setIsConfirmingDisconnect] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const syncMenuNow = async () => {
    setIsSyncing(true);
    try {
      await convex.mutation(api.integrations.square.syncMenuNow, { restaurantId });
      toast.success('Syncing your menu from Square');
    } catch (error) {
      toast.error("Couldn't sync your menu", { description: getUserErrorMessage(error) });
    }
    setIsSyncing(false);
  };

  const disconnect = async () => {
    setIsDisconnecting(true);
    try {
      await convex.action(api.integrations.square.disconnect, { restaurantId });
    } catch (error) {
      toast.error("Couldn't disconnect Square", { description: getUserErrorMessage(error) });
      setIsDisconnecting(false);
      return;
    }
    toast.success('Square disconnected');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex gap-2">
          Square
          <ColorBadge color="green">Connected</ColorBadge>
        </CardTitle>
        <CardDescription>
          Customers pick up their online orders and pay by card. Accept or reject them in Square's
          Order Manager.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {isPickingLocation ? (
          <LocationPicker
            restaurantId={restaurantId}
            location={connection.location}
            onDone={() => setIsPickingLocation(false)}
          />
        ) : (
          connection.location && (
            <div className="grid gap-1">
              <p className="font-medium">Location: {connection.location.name}</p>
              <MenuSyncStatus connection={connection} />
            </div>
          )
        )}
      </CardContent>
      <CardFooter className="gap-2 border-t">
        {connection.location && !isPickingLocation && (
          <>
            <ActionButton loading={isSyncing} onClick={syncMenuNow}>
              Sync menu now
            </ActionButton>
            <ActionButton variant="outline" onClick={() => setIsPickingLocation(true)}>
              Change location
            </ActionButton>
          </>
        )}
        <ActionButton variant="outline" onClick={() => setIsConfirmingDisconnect(true)}>
          Disconnect
        </ActionButton>
      </CardFooter>
      <AlertDialog open={isConfirmingDisconnect} onOpenChange={setIsConfirmingDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Square?</AlertDialogTitle>
            <AlertDialogDescription>
              Your menu stays as it is, and you can edit it here again. Online orders go to your
              OrderHub devices again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton loading={isDisconnecting} onClick={disconnect}>
              Disconnect
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const MenuSyncStatus = ({ connection }: { connection: SquareConnectionPublicCvx }) => {
  if (connection.menuSyncError) {
    return (
      <>
        <p className="text-destructive text-sm">
          Your menu didn't sync. Try again, or disconnect Square and connect it again.
        </p>
        {/* For OrderHub support */}
        <p className="text-muted-foreground break-all text-xs">{connection.menuSyncError}</p>
      </>
    );
  }
  if (connection.menuSyncedAt === null) {
    return <p className="text-muted-foreground text-sm">Syncing your menu from Square…</p>;
  }
  return (
    <p className="text-muted-foreground text-sm">
      Menu synced {formatDistanceToNow(connection.menuSyncedAt, { addSuffix: true })}
    </p>
  );
};

type PickableSquareLocation = FunctionReturnType<
  typeof api.integrations.square.listLocations
>[number];

/**
 * The seller's locations, to pick the one whose menu and orders the venue gets. Picking one
 * replaces the OrderHub menu with its Square menu. One another venue uses can't be picked.
 */
const LocationPicker = ({
  restaurantId,
  location,
  onDone,
}: {
  restaurantId: string;
  location: SquareLocation | null;
  onDone: () => void;
}) => {
  const convex = useConvex();
  const [locations, setLocations] = useState<PickableSquareLocation[]>();
  const [locationsError, setLocationsError] = useState<string>();
  const [selectedLocationId, setSelectedLocationId] = useState(location?.id);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const selectedLocation = locations?.find(({ id }) => id === selectedLocationId);

  // From Square each time, so a location the seller just opened shows up
  useEffect(() => {
    convex
      .action(api.integrations.square.listLocations, { restaurantId })
      .then(setLocations)
      .catch((error: unknown) => setLocationsError(getUserErrorMessage(error)));
  }, [convex, restaurantId]);

  const save = async () => {
    if (!selectedLocationId) return;
    setIsSaving(true);
    try {
      await convex.action(api.integrations.square.setLocation, {
        restaurantId,
        locationId: selectedLocationId,
      });
    } catch (error) {
      toast.error("Couldn't change location", { description: getUserErrorMessage(error) });
      setIsSaving(false);
      return;
    }
    toast.success('Syncing your menu from Square');
    setIsConfirming(false);
    onDone();
  };

  if (locationsError) {
    return <ErrorAlert title="Couldn't load your Square locations" message={locationsError} />;
  }
  if (locations === undefined) return <LoadingIcon />;
  return (
    <div className="grid gap-3">
      <p className="font-medium">Which Square location takes your online orders?</p>
      <div className="flex gap-2">
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger aria-label="Square location" className="w-[280px]">
            <SelectValue placeholder="Pick a location" />
          </SelectTrigger>
          <SelectContent>
            {locations.map(({ id, name, isLinkedToAnotherVenue }) => (
              <SelectItem key={id} value={id} disabled={isLinkedToAnotherVenue}>
                {isLinkedToAnotherVenue ? `${name} (used by another venue)` : name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ActionButton
          disabled={!selectedLocation || selectedLocation.id === location?.id}
          onClick={() => setIsConfirming(true)}
        >
          Use this location
        </ActionButton>
        {location && (
          <ActionButton variant="outline" onClick={onDone}>
            Cancel
          </ActionButton>
        )}
      </div>
      <AlertDialog open={isConfirming} onOpenChange={setIsConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Use {selectedLocation?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Your menu here will be replaced with this location's Square menu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <ActionButton loading={isSaving} onClick={save}>
              Yes
            </ActionButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/** What the Square menu has that customers can't order online, for staff to fix in Square. */
const SkippedCard = ({ skipped }: { skipped: SquareSkippedObject[] }) => (
  <Card>
    <CardHeader>
      <CardTitle>Not on your online menu</CardTitle>
      <CardDescription>
        Change these in Square, and they'll show up next time your menu syncs.
      </CardDescription>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>In Square</TableHead>
            <TableHead>Why</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {skipped.map(({ squareObjectId, name, reason }) => (
            <TableRow key={`${squareObjectId}:${name}:${reason}`}>
              <TableCell>{name}</TableCell>
              <TableCell>{reason}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);
